import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const features = [
  {
    icon: 'ri-camera-lens-line',
    title: 'Photography-First Design',
    detail: 'Full-bleed hero imagery, fast-loading galleries, and layouts built to let your photography sell the experience before a word is read.',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Mobile-First for Travelling Searchers',
    detail: 'Over 80% of tourism searches happen on mobile. Your site loads fast, looks perfect, and converts on every screen size.',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Booking-Optimised Layouts',
    detail: 'Clear booking paths, integrated with Rezdy, FareHarbor, or your own system — reducing friction between interest and confirmed booking.',
  },
  {
    icon: 'ri-google-line',
    title: 'SEO Built In From Day One',
    detail: 'Local SEO, keyword architecture, schema markup, and page speed — not bolted on after, but built into the foundation.',
  },
  {
    icon: 'ri-brain-line',
    title: 'GEO / AEO — AI Search Visibility',
    detail: 'Content and entity signals structured so ChatGPT, Gemini, and Perplexity recommend your tourism experience when travellers ask AI for advice.',
  },
  {
    icon: 'ri-star-line',
    title: 'Review & Social Proof Integration',
    detail: 'Google reviews, TripAdvisor ratings, and testimonials displayed prominently — because social proof converts travellers who have never heard of you.',
  },
  {
    icon: 'ri-speed-line',
    title: 'Speed Optimised',
    detail: 'Travellers on mobile data have zero patience. We optimise every image, script, and server response for sub-2-second load times.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Aggregator Defence Strategy',
    detail: 'We build content and SEO signals that help your direct website outrank Viator and Booking.com for your own brand and experience terms.',
  },
  {
    icon: 'ri-sun-line',
    title: 'Seasonal Content Architecture',
    detail: 'Content strategy designed to capture year-round search intent — not just peak season — so your site generates enquiries in shoulder months too.',
  },
];

const tourismTypes = [
  { icon: 'ri-anchor-line', label: 'Reef & Dive Operators' },
  { icon: 'ri-ship-line', label: 'Ferry Services' },
  { icon: 'ri-compass-3-line', label: 'Adventure Charters' },
  { icon: 'ri-home-4-line', label: 'Accommodation' },
  { icon: 'ri-leaf-line', label: 'Wildlife & Nature Tours' },
  { icon: 'ri-sailboat-line', label: 'Sailing & Boat Hire' },
  { icon: 'ri-landscape-line', label: 'Eco & Cultural Experiences' },
  { icon: 'ri-camera-3-line', label: 'Photography Tours' },
];

export default function TourismFeaturesSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="tourism-features" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-border to-transparent" />
      <div className="absolute bottom-1/3 left-0 w-80 h-80 bg-ds-red/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16 reveal">
          <span className="section-label">What We Build</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mt-3">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              EVERY TOURISM
              <br />
              WEBSITE INCLUDES
              <br />
              <span className="text-ds-red neon-text-red">ALL OF THIS.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs">
              Not add-ons. Not extras. Every feature below is standard in every tourism website we build — because leaving any of them out means leaving bookings on the table.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.4)' }} />
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ds-border mb-16 reveal">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="group bg-ds-bg-2 p-7 hover:bg-ds-bg-3 transition-colors duration-300 flex gap-4"
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              <div className="w-9 h-9 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 flex-shrink-0 mt-0.5">
                <i className={`${f.icon} text-ds-red text-base`} />
              </div>
              <div>
                <h3 className="font-display text-white text-sm tracking-wider leading-tight mb-2">
                  {f.title}
                </h3>
                <p className="font-body text-white/45 text-xs leading-relaxed group-hover:text-white/60 transition-colors duration-300">
                  {f.detail}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Tourism types band */}
        <div className="reveal">
          <div
            className="border border-ds-red/25 bg-ds-red/4 p-8"
            style={{ borderLeft: '3px solid #B4FF39' }}
          >
            <p className="font-display text-white text-base tracking-wider mb-6">
              Tourism &amp; Hospitality Businesses We Serve
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {tourismTypes.map((type) => (
                <div key={type.label} className="flex items-center gap-3 group">
                  <div className="w-8 h-8 flex items-center justify-center border border-ds-border group-hover:border-ds-red/40 transition-colors duration-200 flex-shrink-0">
                    <i className={`${type.icon} text-ds-red text-sm`} />
                  </div>
                  <span className="font-body text-white/60 text-xs leading-tight group-hover:text-white/80 transition-colors duration-200">
                    {type.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
